import { useState, useEffect, useRef, useCallback } from "react";
import { debounce } from "lodash";
import { useInspectionAttributes } from "../../../hooks/query";
import { dedupeInspectionAttributes, findAttributeByName } from "../../../utils/inspectionAttributes";

type ActionType = "R&R" | "Remove" | "Install" | "D&R";

interface IUseInspectionAttribute {
    project: any;
    attributes: any[];
    setAttributes: (e: any) => void;
}

export default function useInspectionAttribute(props: IUseInspectionAttribute) {
    const { project, attributes, setAttributes } = props;
    const { useUpdateSingleAttribute } = useInspectionAttributes();
    const updateAttributeSvc = useUpdateSingleAttribute();

    // --- Visual measurement state ---
    const [visualData, setVisualData] = useState<any[]>([]);
    const [actions, setActions] = useState<Record<string, ActionType>>({
        "Roof Vents": "R&R",
        "Box Vents": "R&R",
        "Turbine Vents": "R&R",
        "Plumbing Jacks": "R&R",
        "Split Boots": "R&R",
        "Furnace Vents": "R&R",
        "Bathroom Vents": "R&R",
        "Dryer Vents": "R&R",
        Chimney: "R&R",
        Skylights: "R&R",
        "Step Flashing": "Install",
        "Kick-out Diverter": "R&R",
        "Ice & Water Barrier": "Install",
        "Cornice Returns": "R&R",
        "Cornice Strip": "R&R",
        "Sheathing": "R&R",
        "Fascia Board": "R&R",
        "Soffit": "R&R",
        "Gutter Apron": "R&R",
        "Gutter/Downspout": "R&R",
        "Paint Gutter": "Install",
        "Low Slope": "R&R",
        "Counter Flashing": "Install",
        "Head Wall Metal": "Install",
    });

    const visualItems = [
        "Roof Vents",
        "Box Vents",
        "Turbine Vents",
        "Plumbing Jacks",
        "Furnace Vents",
        "Bathroom Vents",
        "Dryer Vents",
        "Split Boots",
        "Chimney",
        "Skylights",
        "High Roof",
        "Ice & Water Barrier",
        "Step Flashing",
        "Kick-out Diverter",
        "Low Slope",
        "Counter Flashing",
        "Head Wall Metal",
        "Cornice Returns",
        "Cornice Strip",
        "Fascia Board",
        "Gutter Apron",
        "Sheathing",
        "Paint Gutter",
    ];

    const processVisualData = useCallback(() => {
        if (!attributes || attributes.length === 0) return;

        const newVisualData: any[] = [];
        const newActions: Record<string, ActionType> = {};

        visualItems.forEach((name) => {
            const attr = findAttributeByName(attributes, name);
            if (attr) {
                newVisualData.push({
                    name,
                    actual: attr.Qty !== null ? Number(attr.Qty) : 0,
                    id: attr.Id,
                });
                if (attr.Action) {
                    newActions[name] = attr.Action as ActionType;
                }
            } else {
                newVisualData.push({ name, actual: 0, id: null });
            }
        });

        // Also process Ancillary items that might not be in visualItems but are on the page
        const ancillaryItems = [
            "Cornice Returns",
            "Cornice Strip",
            "Fascia Board",
            "Soffit",
            "Gutter Apron",
            "Sheathing",
            "Gutter/Downspout",
            "Paint Gutter",
        ];
        ancillaryItems.forEach((name) => {
            const attr = findAttributeByName(attributes, name);
            if (attr && attr.Action) {
                newActions[name] = attr.Action as ActionType;
            }
        });

        setVisualData(newVisualData);
        setActions((prev) => ({ ...prev, ...newActions }));
    }, [attributes]);

    useEffect(() => {
        processVisualData();
    }, [processVisualData]);

    // ─── Debounced API sync (fires after 500ms of inactivity) ───────────────────
    // Accepts previousValue, previousQty, previousAction so it can revert on failure.
    const debouncedAttributeUpdate = useRef(
        debounce(
            async (params: {
                Id?: string;
                ProjectId?: string;
                AttributeName?: string;
                Value?: any;
                Qty?: number;
                Action?: string;
                previousValue?: any;
                previousQty?: number;
                previousAction?: string;
                isNewEntry?: boolean;
            }) => {
                const { previousValue, previousQty, previousAction, isNewEntry, ...apiParams } = params;
                try {
                    const response = await updateAttributeSvc.mutateAsync(apiParams);
                    const finalId = apiParams.Id || response?.Id;

                    // Sync the server-confirmed Id back into local state (important for new entries)
                    setAttributes((prev: any[]) => {
                        const merged = prev.map((a: any) => {
                            if (
                                (apiParams.Id && a.Id === apiParams.Id) ||
                                (apiParams.AttributeName && a.AttributeName === apiParams.AttributeName)
                            ) {
                                return { ...a, Id: finalId };
                            }
                            return a;
                        });
                        return dedupeInspectionAttributes(merged);
                    });
                } catch (error) {
                    console.error("[InspectionAttribute] API failed, reverting:", apiParams.AttributeName, error);

                    if (isNewEntry) {
                        // New attribute — remove the optimistically-added entry
                        setAttributes((prev: any[]) =>
                            prev.filter((a: any) => a.AttributeName !== apiParams.AttributeName),
                        );
                    } else if (apiParams.AttributeName !== undefined) {
                        // Existing attribute — revert to previous values
                        setAttributes((prev: any[]) =>
                            prev.map((a: any) => {
                                if (a.AttributeName === apiParams.AttributeName) {
                                    return {
                                        ...a,
                                        AttributeValue: previousValue !== undefined ? previousValue : a.AttributeValue,
                                        Qty: previousQty !== undefined ? previousQty : a.Qty,
                                        Action: previousAction !== undefined ? previousAction : a.Action,
                                    };
                                }
                                return a;
                            }),
                        );
                    }
                }
            },
            500,
        ),
    ).current;

    useEffect(() => () => debouncedAttributeUpdate.cancel(), [debouncedAttributeUpdate]);

    const handleVisualInput = (itemName: string, value: number) => {
        setVisualData((prev) => prev.map((item) => (item.name === itemName ? { ...item, actual: value } : item)));

        const attr = findAttributeByName(attributes, itemName);
        const previousQty = attr?.Qty !== null && attr?.Qty !== undefined ? Number(attr.Qty) : 0;
        // Prefer what's already saved in DB, then fall back to the visual actions state, then "R&R"
        const currentAction = attr?.Action || actions[itemName] || "R&R";
        const isNewEntry = !attr?.Id;

        // 1. Apply change to parent state immediately — also carry action forward to prevent stale reads
        setAttributes((prev: any[]) => {
            const deduped = dedupeInspectionAttributes(prev);
            const exists = deduped.some((a: any) => a.AttributeName === itemName);
            const next = !exists
                ? [...deduped, { Id: null, AttributeName: itemName, Qty: value, Action: currentAction }]
                : deduped.map((a: any) =>
                      a.AttributeName === itemName ? { ...a, Qty: value, Action: currentAction } : a,
                  );
            return dedupeInspectionAttributes(next);
        });

        // 2. Sync to backend (debounced), carry rollback context
        debouncedAttributeUpdate({
            Id: attr?.Id,
            ProjectId: isNewEntry ? project?.id : undefined,
            AttributeName: isNewEntry ? itemName : undefined,
            Qty: value,
            Action: currentAction,
            previousQty,
            previousAction: currentAction,
            isNewEntry,
        });
    };

    const handleActionChange = (itemName: string, newAction: ActionType) => {
        setActions((prev) => ({ ...prev, [itemName]: newAction }));

        const attr = findAttributeByName(attributes, itemName);
        const qty = visualData.find((v) => v.name === itemName)?.actual || 0;
        const previousQty = attr?.Qty !== null && attr?.Qty !== undefined ? Number(attr.Qty) : qty;
        const previousAction = attr?.Action || actions[itemName] || "R&R";
        const isNewEntry = !attr?.Id;

        // 1. Apply change to parent state immediately — UI responds instantly and keeps state in sync
        setAttributes((prev: any[]) => {
            const deduped = dedupeInspectionAttributes(prev);
            const exists = deduped.some((a: any) => a.AttributeName === itemName);
            const next = !exists
                ? [...deduped, { Id: null, AttributeName: itemName, Qty: qty, Action: newAction }]
                : deduped.map((a: any) =>
                      a.AttributeName === itemName ? { ...a, Action: newAction } : a,
                  );
            return dedupeInspectionAttributes(next);
        });

        // 2. Sync to backend (debounced), carry rollback context
        debouncedAttributeUpdate({
            Id: attr?.Id,
            ProjectId: isNewEntry ? project?.id : undefined,
            AttributeName: isNewEntry ? itemName : undefined,
            Qty: qty,
            Action: newAction,
            previousQty,
            previousAction,
            isNewEntry,
        });
    };

    const getAttrValue = (name: string) => {
        const attr = findAttributeByName(attributes, name);
        if (!attr) return null;
        const val = attr.AttributeValue;
        if (val !== null && val !== undefined && String(val).trim() !== "") {
            return val;
        }
        return val ?? null;
    };

    const applyAttributeValue = useCallback(
        (attributeName: string, value: string) => {
            const attr = findAttributeByName(attributes, attributeName);
            const previousValue = attr?.AttributeValue;
            const isNewEntry = !attr?.Id;

            setAttributes((prev: any[]) => {
                const deduped = dedupeInspectionAttributes(prev);
                const exists = deduped.some((a: any) => a.AttributeName === attributeName);
                const next = !exists
                    ? [...deduped, { Id: null, AttributeName: attributeName, AttributeValue: value }]
                    : deduped.map((a: any) =>
                          a.AttributeName === attributeName ? { ...a, AttributeValue: value } : a,
                      );
                return dedupeInspectionAttributes(next);
            });

            debouncedAttributeUpdate({
                Id: attr?.Id,
                ProjectId: isNewEntry ? project?.id : undefined,
                AttributeName: isNewEntry ? attributeName : undefined,
                Value: value,
                previousValue,
                isNewEntry,
            });
        },
        [attributes, debouncedAttributeUpdate, project?.id, setAttributes],
    );

    const GUTTER_DATA_FIELDS = new Set(["Gutter Length", "Gutter Size", "Gutter Color"]);
    const DOWNSPOUT_DATA_FIELDS = new Set(["Downspout Length", "Downspout Size", "Downspout Color"]);

    const syncGutterScopeFlags = useCallback(
        (overrides?: Record<string, string>) => {
            const read = (name: string) => {
                if (overrides && name in overrides) return String(overrides[name]);
                const val = getAttrValue(name);
                return val !== null && val !== undefined ? String(val) : "";
            };

            const gutterLength = Number(read("Gutter Length")) || 0;
            const gutterSize = read("Gutter Size").trim();
            const hasGutterData = gutterLength > 0 || gutterSize !== "";

            const downspoutLength = Number(read("Downspout Length")) || 0;
            const downspoutSize = read("Downspout Size").trim();
            const hasDownspoutData = downspoutLength > 0 || downspoutSize !== "";

            const gutterRemove = read("Remove Existing Gutters");
            const gutterInstall = read("Install New Gutters");
            const downspoutRemove = read("Remove Existing Downspouts");
            const downspoutInstall = read("Install New Downspouts");

            if (hasGutterData && gutterRemove.toLowerCase() !== "true" && gutterInstall.toLowerCase() !== "true") {
                applyAttributeValue("Remove Existing Gutters", "true");
                applyAttributeValue("Install New Gutters", "true");
            }

            if (
                hasDownspoutData &&
                downspoutRemove.toLowerCase() !== "true" &&
                downspoutInstall.toLowerCase() !== "true"
            ) {
                applyAttributeValue("Remove Existing Downspouts", "true");
                applyAttributeValue("Install New Downspouts", "true");
            }
        },
        [applyAttributeValue, attributes],
    );

    const resetVentilationDependentAttributes = useCallback(
        (newVentilationType: string) => {
            handleVisualInput("Roof Vents", 0);
            handleVisualInput("Box Vents", 0);
            handleVisualInput("Turbine Vents", 0);
            applyAttributeValue("Ventilation Painted", newVentilationType === "Ridge Vent" ? "N/A" : "No");
        },
        [applyAttributeValue, handleVisualInput],
    );

    // ─── Optimistic update: instant UI, debounced API, rollback on failure ──────
    const handleUpdate = (value: string, attributeName: string) => {
        const attr = findAttributeByName(attributes, attributeName);
        const previousValue = attr?.AttributeValue;

        if (attributeName === "Ventilation Type" && previousValue && previousValue !== value) {
            resetVentilationDependentAttributes(value);
        }

        applyAttributeValue(attributeName, value);

        if (GUTTER_DATA_FIELDS.has(attributeName) || DOWNSPOUT_DATA_FIELDS.has(attributeName)) {
            syncGutterScopeFlags({ [attributeName]: value });
        }
    };

    const getVisualItem = (name: string) => visualData.find((v) => v.name === name);

    return {
        visualData,
        actions,
        handleVisualInput,
        handleActionChange,
        handleUpdate,
        getAttrValue,
        getVisualItem,
    };
}
